// The Family control on the part page. It lives outside the page's edit mode on
// purpose: the server lets family editors (procurement, manager-manufacturing)
// change ONLY the family, and refuses a request that mixes it with other
// fields, so the family is saved in its own request the moment it is chosen.

import React, { useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import { Field } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import { Tooltip } from '../components/ui/Tooltip';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import type { PartFamilyRow } from '../Receiving/types';
import { canEditFamilies } from './familyAccess';

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
const textButton =
  `rounded px-1 text-xs text-on-surface-variant hover:text-primary transition-colors ${FOCUS} `
  + 'disabled:opacity-40';

export interface PartFamilyFieldProps {
  partId: number;
  familyId: number | null;
  familyLabel: string | null;
  families: PartFamilyRow[];
  /** The part now belongs to this family (or to none). */
  onChanged: (next: { part_family_id: number | null; family_label: string | null }) => void;
  /** A family was created or renamed; the parent re-reads the list. */
  onFamiliesChanged: () => void;
}

type Mode = 'view' | 'creating' | 'renaming';

const PartFamilyField: React.FC<PartFamilyFieldProps> = ({
  partId,
  familyId,
  familyLabel,
  families,
  onChanged,
  onFamiliesChanged,
}) => {
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  const canEditFamily = canEditFamilies(user);

  const [mode, setMode] = useState<Mode>('view');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // What the select shows while its PATCH is in flight. Null means "the saved value".
  const [pending, setPending] = useState<number | '' | null>(null);
  const selectRef = useRef<HTMLButtonElement>(null);

  if (!canEditFamily) return <>{familyLabel || '—'}</>;

  // The select comes back when the inline form closes; give it focus back so a
  // keyboard user lands where they started.
  const close = () => {
    setMode('view');
    setError(null);
    setName('');
    setTimeout(() => selectRef.current?.focus(), 0);
  };

  const assign = async (id: number | null, label: string | null) => {
    await api.patch(`/parts-catalog/${partId}`, { body: { part_family_id: id } });
    onChanged({ part_family_id: id, family_label: label });
  };

  const choose = async (value: string) => {
    // Ignored rather than disabled while a save is in flight: disabling the
    // select would drop keyboard focus mid-arrow.
    if (pending !== null) return;
    const id = value ? parseInt(value, 10) : null;
    setPending(id ?? '');
    try {
      await assign(id, id == null ? null : families.find((f) => f.id === id)?.label ?? null);
    } catch (err: any) {
      toast.error(err?.message || 'Could not change the family');
    } finally {
      setPending(null);
    }
  };

  const create = async () => {
    const label = name.trim();
    if (!label) {
      setError('Name the family first.');
      return;
    }
    if (busy) return;
    setBusy(true);
    setError(null);
    let created: PartFamilyRow;
    try {
      created = (await api.post('/part-families', { body: { label } })) as PartFamilyRow;
    } catch (err: any) {
      setError(err?.message || 'Could not create the family');
      setBusy(false);
      return;
    }
    onFamiliesChanged();
    try {
      await assign(created.id, created.label);
      close();
    } catch (err: any) {
      toast.error(`Created ${created.label}, but could not add this part to it: ${err?.message || 'try again'}`);
      close();
    } finally {
      setBusy(false);
    }
  };

  const rename = async () => {
    const label = name.trim();
    if (!label) {
      setError('A family needs a name.');
      return;
    }
    if (label === familyLabel) {
      close();
      return;
    }
    if (busy || familyId == null) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/part-families/${familyId}`, { body: { label } });
      onChanged({ part_family_id: familyId, family_label: label });
      onFamiliesChanged();
      close();
    } catch (err: any) {
      setError(err?.message || 'Could not rename the family');
    } finally {
      setBusy(false);
    }
  };

  const nameKeys = (submit: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };

  if (mode !== 'view') {
    const creating = mode === 'creating';
    return (
      <div className="space-y-1">
        <Field
          size="sm"
          autoFocus
          value={name}
          maxLength={80}
          disabled={busy}
          aria-label={creating ? 'New family name' : 'Family name'}
          placeholder={creating ? 'Name the family' : undefined}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={nameKeys(creating ? create : rename)}
        />
        <p className="text-xs text-on-surface-variant">
          {creating ? 'Parts that can stand in for each other.' : 'Renames this family everywhere.'}
        </p>
        <Alert show={!!error} variant="text" className="!text-xs">
          {error}
        </Alert>
        <div className="flex items-center gap-2 pt-0.5">
          <button
            type="button"
            onClick={creating ? create : rename}
            disabled={busy}
            className={buttonClass('filled', 'sm')}
          >
            {creating ? 'Create' : 'Save'}
          </button>
          <button type="button" onClick={close} disabled={busy} className={textButton}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  const shown = pending ?? (familyId ?? '');
  // The current family stays listed even once retired, or the select would
  // show "(none)" for a part that has one.
  const options = families.filter((f) => f.is_active || f.id === familyId);

  return (
    <div className="flex items-center gap-2 min-w-0">
      <Select
        ref={selectRef}
        label="Family"
        size="sm"
        className="min-w-0 flex-1"
        aria-busy={pending !== null}
        value={String(shown)}
        placeholder="(none)"
        options={[
          { key: '', label: '(none)' },
          ...options.map((f) => ({ key: String(f.id), label: f.label })),
        ]}
        onChange={choose}
        actions={[
          {
            key: 'new-family',
            label: 'New family...',
            onSelect: () => {
              setName('');
              setError(null);
              setMode('creating');
            },
          },
        ]}
      />
      <Appear show={familyId != null} className="shrink-0">
        <Tooltip label="Renames this family everywhere">
          <button
            type="button"
            onClick={() => {
              setName(familyLabel || '');
              setError(null);
              setMode('renaming');
            }}
            className={`${textButton} shrink-0`}
          >
            Rename
          </button>
        </Tooltip>
      </Appear>
    </div>
  );
};

export default PartFamilyField;
